import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import { MessageCircle, Bot, User, ArrowRight } from 'lucide-react'
import { MarkdownContent } from '@/components/chat/MarkdownContent'
import { useTheme } from '@/contexts/ThemeContext'

interface Message {
  id: string
  content: string
  is_user: boolean
  timestamp: string
}

interface SharedConversation {
  id: string
  title: string
  created_at: string
  messages: Message[]
}

const assistantBaseUrl = import.meta.env.VITE_ASSISTANT_BASE_URL || 'http://localhost:3004'

export function SharedConversationPage() {
  const { token } = useParams<{ token: string }>()
  const { isDark } = useTheme()
  const [conversation, setConversation] = useState<SharedConversation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<'not_found' | 'expired' | 'error' | null>(null)

  useEffect(() => {
    if (!token) return
    axios
      .get(`${assistantBaseUrl}/assistant/conversations/shared/${token}`)
      .then(res => setConversation(res.data))
      .catch(err => {
        if (err.response?.status === 410) setError('expired')
        else if (err.response?.status === 404) setError('not_found')
        else setError('error')
      })
      .finally(() => setLoading(false))
  }, [token])

  const Logo = () => (
    <a href="/" className="inline-flex">
      <img
        src={isDark ? '/assets/logos/Logotipo-Verde-Broto.svg' : '/assets/logos/Logotipo-Verde.svg'}
        alt="RegenerAI"
        className="h-10 sm:h-12 w-auto"
      />
    </a>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-primary border-t-transparent" />
      </div>
    )
  }

  if (error) {
    const messages = {
      expired: { title: 'Link expirado', desc: 'Este link de compartilhamento expirou. Peça ao dono da conversa que gere um novo link.' },
      not_found: { title: 'Conversa não encontrada', desc: 'Este link não existe ou foi revogado.' },
      error: { title: 'Algo deu errado', desc: 'Não foi possível carregar a conversa. Tente novamente.' },
    }
    const { title, desc } = messages[error]
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center px-4 gap-6">
        <Logo />
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-brand-surface-alt rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-brand-text-muted" />
          </div>
          <h1 className="text-xl font-bold text-brand-text mb-2">{title}</h1>
          <p className="text-brand-text-secondary text-sm mb-6">{desc}</p>
          <Link to="/login" className="btn-primary inline-flex items-center gap-2">
            Acessar RegenerAI
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Sticky header: logo only */}
      <div className="sticky top-0 z-10 bg-brand-surface border-b border-brand-border">
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <Logo />
          <span className="text-xs text-brand-text-muted hidden sm:block">Conversa compartilhada</span>
        </div>
      </div>

      {/* Conversation title — centered, scrollable */}
      <div className="bg-brand-surface border-b border-brand-border">
        <div className="max-w-3xl mx-auto px-4 py-4 text-center sm:py-5">
          <h1 className="text-base sm:text-lg font-semibold text-brand-text leading-snug">
            {conversation?.title}
          </h1>
          <p className="text-xs text-brand-text-muted mt-1 sm:hidden">Conversa compartilhada</p>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-32">
        {conversation?.messages.map(msg => (
          <div
            key={msg.id}
            className={`flex gap-3 ${msg.is_user ? 'flex-row-reverse' : 'flex-row'}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.is_user ? 'bg-brand-primary' : 'bg-brand-accent'
              }`}>
              {msg.is_user
                ? <User className="w-4 h-4 text-white" />
                : <Bot className="w-4 h-4 text-verde-500" />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${msg.is_user
              ? 'bg-brand-primary text-white rounded-tr-sm'
              : 'bg-brand-surface border border-brand-border text-brand-text rounded-tl-sm'
              }`}>
              {msg.is_user
                ? <p className="whitespace-pre-wrap">{msg.content}</p>
                : <MarkdownContent content={msg.content} className="leading-relaxed" />}
            </div>
          </div>
        ))}
      </div>

      {/* CTA Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-brand-surface border-t border-brand-border">
        <div className="max-w-3xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-center sm:text-left">
            <p className="text-sm font-semibold text-brand-text">Quer conversar com a IA da RegenerAI?</p>
            <p className="text-xs text-brand-text-muted">Acesse gratuitamente e tire suas dúvidas sobre agricultura regenerativa</p>
          </div>
          <Link
            to="/register"
            className="btn-primary whitespace-nowrap flex items-center gap-2"
          >
            Criar conta grátis
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  )
}
